// Trazabilidad bidireccional de UTEs.
// - Si la entidad es UTE → muestra empresas socias.
// - Si la entidad participa en UTEs → muestra en cuáles.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Building2, Users, Loader2, ArrowRight, Link2 } from 'lucide-react';
import { entidadesApi, resolveAssetUrl } from '../../services/api';

const Avatar = ({ src, nombre }) => (
  <div className="h-8 w-8 rounded bg-ink-100 text-ink-500 flex items-center justify-center shrink-0 overflow-hidden">
    {src ? <img src={resolveAssetUrl(src)} alt="" className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} /> : <Building2 className="w-4 h-4" />}
  </div>
);

const Fila = ({ empresa, onClick, sufijo, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left px-3 py-2 hover:bg-ink-50 rounded flex items-center gap-3 border border-transparent hover:border-ink-200"
    data-testid={testid}
  >
    <Avatar src={empresa.logo_url} />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-ink-800 truncate">
        {empresa.razon_social}
        {empresa.es_lead && <Badge className="ml-2 border bg-violet-50 border-violet-200 text-violet-700 text-[9px]">LEAD</Badge>}
      </div>
      <div className="text-xs text-ink-500 truncate">
        {empresa.cif || '—'} · {empresa.naturaleza_juridica || empresa.estado || '—'}
      </div>
    </div>
    {sufijo}
    <ArrowRight className="w-4 h-4 text-ink-400 shrink-0" />
  </button>
);

const UteTrazabilidad = ({ entidadId, esUte }) => {
  const navigate = useNavigate();
  const [socios, setSocios] = useState([]);
  const [utesDonde, setUtesDonde] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entidadId) return;
    setLoading(true);
    Promise.all([
      esUte ? entidadesApi.uteSocios(entidadId).catch(() => ({ socios: [] })) : Promise.resolve({ socios: [] }),
      entidadesApi.utesDondeParticipo(entidadId).catch(() => ({ utes: [] })),
    ]).then(([s, u]) => {
      setSocios(s.socios || []);
      setUtesDonde(u.utes || []);
    }).finally(() => setLoading(false));
  }, [entidadId, esUte]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ink-500 py-2 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando UTE…
      </div>
    );
  }

  if (socios.length === 0 && utesDonde.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="ute-trazabilidad">
      {esUte && socios.length > 0 && (
        <Card className="p-4 border-violet-200 bg-gradient-to-br from-violet-50/80 to-white" data-testid="ute-socios">
          <h3 className="text-sm font-semibold text-violet-700 mb-2 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Empresas socias de esta UTE ({socios.length})
          </h3>
          <p className="text-xs text-ink-500 mb-2">Esta entidad es una UTE. Las empresas que la conforman:</p>
          <div className="space-y-1">
            {socios.map((s) => (
              <Fila
                key={s.id} empresa={s}
                onClick={() => navigate(`/clientes/${s.id}`)}
                testid={`ute-socio-${s.id}`}
              />
            ))}
          </div>
        </Card>
      )}

      {utesDonde.length > 0 && (
        <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50/80 to-white" data-testid="utes-donde-participo">
          <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
            <Users className="w-4 h-4" /> UTEs en las que participa ({utesDonde.length})
          </h3>
          <p className="text-xs text-ink-500 mb-2">Esta empresa forma parte de las siguientes uniones temporales:</p>
          <div className="space-y-1">
            {utesDonde.map((u) => (
              <Fila
                key={u.id} empresa={u}
                onClick={() => navigate(`/clientes/${u.id}`)}
                sufijo={<Badge className="bg-amber-100 border-amber-300 text-amber-800 border text-[10px] shrink-0">UTE</Badge>}
                testid={`ute-donde-${u.id}`}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default UteTrazabilidad;
